import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import type { CostTrend, CostService, CostResourceGroup, CostResource, CostDashboard } from '@/types/api'
import { Sparkline, StackedArea, TotalArea, colorFor, fmtMoney, fmtPct, fmtDate } from './DriftCharts'
import './driftline.css'

// ── TrendChip ──────────────────────────────────────────────────────────────────

function TrendChip({ pct, abs }: { pct?: number; abs: number }) {
  const up = abs > 0.001
  const down = abs < -0.001
  const cls = up ? 'up' : down ? 'down' : 'flat'
  const sign = up ? '▲' : down ? '▼' : '·'
  let display: string
  if (pct === undefined || !isFinite(pct)) display = fmtMoney(abs)
  else if (Math.abs(pct * 100) > 999) display = (abs > 0 ? '+' : '') + fmtMoney(abs)
  else display = (pct > 0 ? '+' : '') + fmtPct(pct)
  return (
    <span className={`trend-chip ${cls}`}>
      <span style={{ fontSize: 8, lineHeight: 1 }}>{sign}</span>
      {display}
    </span>
  )
}

// ── KPI row ────────────────────────────────────────────────────────────────────

function KpiRow({ data, climberCount }: { data: CostDashboard; climberCount: number }) {
  const { daily, meta } = data
  const n = daily.length
  const total = meta.total
  const dailyAvg = n > 0 ? total / n : 0
  const todayCost = daily[n - 1] ?? 0
  const yesterdayCost = daily[n - 2] ?? 0
  const dayChange = yesterdayCost > 0 ? (todayCost - yesterdayCost) / yesterdayCost : 0
  const lastThreeAvg = daily.slice(-3).reduce((a, b) => a + b, 0) / Math.min(3, n)
  const forecast = lastThreeAvg * 30
  const half = Math.floor(n / 2)
  const fAvg = daily.slice(0, half).reduce((a, b) => a + b, 0) / (half || 1)
  const sAvg = daily.slice(half).reduce((a, b) => a + b, 0) / (n - half || 1)
  const periodPct = fAvg > 0 ? (sAvg - fAvg) / fAvg : 0

  return (
    <div className="kpi-row">
      <div className="kpi">
        <div className="kpi-label">Spend · {n} days</div>
        <div className="kpi-value num">{fmtMoney(total)}<span className="unit">{meta.currency}</span></div>
        <div className="kpi-foot">
          <TrendChip pct={periodPct} abs={sAvg - fAvg} />
          <span>second half vs first</span>
        </div>
        <div className="kpi-spark">
          <Sparkline values={daily} width={120} height={36} color="var(--accent)" fill="color-mix(in oklab, var(--accent) 14%, transparent)" showDot />
        </div>
      </div>

      <div className="kpi">
        <div className="kpi-label">Daily run-rate</div>
        <div className="kpi-value num">{fmtMoney(dailyAvg)}<span className="unit">{meta.currency}/day</span></div>
        <div className="kpi-foot">
          <TrendChip pct={dayChange} abs={todayCost - yesterdayCost} />
          <span>vs prior day</span>
        </div>
        <div className="kpi-spark">
          <Sparkline values={daily} width={120} height={36} color="var(--accent-2)" fill="color-mix(in oklab, var(--accent-2) 14%, transparent)" showDot />
        </div>
      </div>

      <div className="kpi">
        <div className="kpi-label">30-day projection</div>
        <div className="kpi-value num">{fmtMoney(forecast)}<span className="unit">{meta.currency}</span></div>
        <div className="kpi-foot">
          <span className="mono" style={{ color: 'var(--muted)' }}>last-3-day avg × 30</span>
        </div>
      </div>

      <div className="kpi">
        <div className="kpi-label">Climbing resource groups</div>
        <div className="kpi-value num">{climberCount}<span className="unit">rising</span></div>
        <div className="kpi-foot">
          <span style={{ color: 'var(--muted)' }}>cost trending up over period</span>
        </div>
      </div>
    </div>
  )
}

// ── Hero chart ─────────────────────────────────────────────────────────────────

function HeroChart({
  data,
  groupBy,
  onGroupBy,
}: {
  data: CostDashboard
  groupBy: 'service' | 'rg'
  onGroupBy: (v: 'service' | 'rg') => void
}) {
  const [dimmed, setDimmed] = useState(new Set<string>())
  const isSvc = groupBy === 'service'
  const sourceList = isSvc ? data.services : data.resourceGroups
  const TOP_N = 8
  const top = sourceList.slice(0, TOP_N)
  const others = sourceList.slice(TOP_N)
  const otherDaily = data.meta.dates.map((_, i) => others.reduce((a, b) => a + (b.daily[i] ?? 0), 0))
  const otherTotal = others.reduce((a, b) => a + b.total, 0)

  const series = [
    ...top.map((s, i) => ({ name: s.name, total: s.total, daily: s.daily, color: colorFor(i) })),
    ...(others.length > 0 ? [{ name: `Other (${others.length})`, total: otherTotal, daily: otherDaily, color: 'oklch(0.78 0.01 250)' }] : []),
  ]

  function toggle(name: string) {
    setDimmed(prev => {
      const n = new Set(prev)
      if (n.has(name)) n.delete(name); else n.add(name)
      return n
    })
  }

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="card-title">Daily spend</div>
          <div className="card-sub">
            Stacked by {isSvc ? 'service' : 'resource group'} · {data.meta.dates.length} days
          </div>
        </div>
        <div className="segmented">
          <button data-active={groupBy === 'service' ? 'true' : 'false'} onClick={() => onGroupBy('service')}>By service</button>
          <button data-active={groupBy === 'rg' ? 'true' : 'false'} onClick={() => onGroupBy('rg')}>By resource group</button>
        </div>
      </div>
      <div className="hero-grid">
        <StackedArea series={series} dates={data.meta.dates} currency={data.meta.currency} height={320} dimmed={dimmed} />
        <div className="legend">
          {series.map(s => (
            <button key={s.name} className={`legend-item${dimmed.has(s.name) ? ' dim' : ''}`} onClick={() => toggle(s.name)} style={{ all: 'unset', cursor: 'pointer' }}>
              <span className="legend-swatch" style={{ background: s.color }} />
              <span className="legend-name" title={s.name}>{s.name}</span>
              <span className="legend-val">{fmtMoney(s.total)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Climbers panel ─────────────────────────────────────────────────────────────

type ClimberItem = { name: string; total: number; daily: number[]; services?: string[]; trend: CostTrend }

function Climbers({
  items, currency, mode, onSelect,
}: {
  items: ClimberItem[]
  currency: string
  mode: 'climbers' | 'fallers'
  onSelect?: (it: ClimberItem) => void
}) {
  return (
    <div>
      {items.map((it, idx) => {
        const t = it.trend
        const isUp = t.absChange > 0
        const color = mode === 'fallers' ? 'var(--down)' : 'var(--up)'
        const fillCol = mode === 'fallers'
          ? 'color-mix(in oklab, var(--down) 12%, transparent)'
          : 'color-mix(in oklab, var(--up) 12%, transparent)'
        return (
          <div
            className="climber"
            key={it.name}
            onClick={() => onSelect?.(it)}
            style={{ cursor: onSelect ? 'pointer' : 'default' }}
            role={onSelect ? 'button' : undefined}
            tabIndex={onSelect ? 0 : undefined}
            onKeyDown={onSelect ? (e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(it) } : undefined}
          >
            <div className="climber-rank">{String(idx + 1).padStart(2, '0')}</div>
            <div className="climber-main">
              <div className="climber-name">{it.name}</div>
              <div className="climber-meta">
                {fmtMoney(it.total)} {currency} total · {it.services?.length ?? 1} svc
              </div>
            </div>
            <Sparkline values={it.daily} width={88} height={28} color={color} fill={fillCol} strokeWidth={1.4} showDot />
            <div className="climber-delta">
              <div className="abs">{isUp ? '+' : ''}{fmtMoney(t.absChange)}</div>
              <div className="pct" style={{ color: isUp ? 'var(--up)' : 'var(--down)' }}>
                {isUp ? '+' : ''}{isFinite(t.pctChange) ? fmtPct(t.pctChange) : '—'}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Service breakdown ──────────────────────────────────────────────────────────

function ServiceBreakdown({
  services, currency, total, onSelect,
}: {
  services: CostService[]
  currency: string
  total: number
  onSelect?: (s: CostService) => void
}) {
  const top = services.slice(0, 10)
  const max = top[0]?.total || 1
  return (
    <div>
      {top.map((s, i) => (
        <div
          className="bar-row"
          key={s.name}
          onClick={() => onSelect?.(s)}
          style={{ cursor: onSelect ? 'pointer' : 'default' }}
          role={onSelect ? 'button' : undefined}
          tabIndex={onSelect ? 0 : undefined}
          onKeyDown={onSelect ? (e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(s) } : undefined}
        >
          <div className="name" title={s.name}>{s.name}</div>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: (s.total / max * 100) + '%', background: colorFor(i) }} />
          </div>
          <div className="bar-val">{fmtMoney(s.total)}</div>
          <div className="bar-pct">{((s.total / total) * 100).toFixed(1)}%</div>
        </div>
      ))}
      {services.length === 0 && (
        <p style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>
          No service data for this period.
        </p>
      )}
    </div>
  )
}

// ── Resource table ─────────────────────────────────────────────────────────────

const SERVICE_ICONS: Record<string, string> = {
  'Storage':                        'M3 6h18M3 10h18M3 14h18M3 18h18',
  'SQL Database':                   'M3 5c0-1.1 4-2 9-2s9 .9 9 2v14c0 1.1-4 2-9 2s-9-.9-9-2V5z',
  'Azure App Service':              'M4 4h16v6H4zM4 14h16v6H4z',
  'Functions':                      'M9 4l-4 16M15 4l-4 16M4 9h16M3 14h16',
  'Event Hubs':                     'M3 12c4-4 8-4 12 0s8 4 12 0',
  'Azure Cosmos DB':                'M12 3a9 9 0 100 18 9 9 0 000-18zM12 3v18M3 12h18',
  'Container Registry':             'M3 9h6v6H3zM9 5h6v14H9zM15 9h6v6h-6z',
  'Log Analytics':                  'M3 19h18M5 15l4-6 4 4 4-8',
  'Azure DevOps':                   'M4 7l8-4 8 4-8 4zM4 7v10l8 4 8-4V7M4 7l8 4 8-4',
  'Redis Cache':                    'M3 5h18v4H3zM3 11h18v4H3zM3 17h18v2H3z',
  'API Management':                 'M6 4h12v4H6zM6 10h12v4H6zM6 16h12v4H6z',
  'Microsoft Defender for Cloud':   'M12 3l9 4v6c0 5-4 8-9 9-5-1-9-4-9-9V7l9-4z',
  'Azure Container Apps':           'M3 12l9-5 9 5-9 5zM3 12v6l9 5 9-5v-6',
  'Microsoft Fabric':               'M12 3l9 9-9 9-9-9z',
}

function ServiceIcon({ service }: { service: string }) {
  const d = SERVICE_ICONS[service] ?? 'M4 4h16v16H4zM4 10h16M10 4v16'
  return (
    <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

function ResourceTable({
  resources, currency, onSelect, selected, filterText, filterService, filterRg,
}: {
  resources: CostResource[]
  currency: string
  onSelect: (r: CostResource) => void
  selected: CostResource | null
  filterText: string
  filterService: string
  filterRg: string
}) {
  const [sortBy, setSortBy] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'total', dir: 'desc' })

  const filtered = useMemo(() => {
    let arr = resources
    if (filterText) {
      const q = filterText.toLowerCase()
      arr = arr.filter(r =>
        r.name.toLowerCase().includes(q) || r.rg.toLowerCase().includes(q) || r.id.toLowerCase().includes(q)
      )
    }
    if (filterService !== 'All') arr = arr.filter(r => r.service === filterService)
    if (filterRg !== 'All') arr = arr.filter(r => r.rg === filterRg)
    const dir = sortBy.dir === 'asc' ? 1 : -1
    return [...arr].sort((a, b) => {
      if (sortBy.key === 'total')  return dir * (a.total - b.total)
      if (sortBy.key === 'change') return dir * (a.trend.absChange - b.trend.absChange)
      if (sortBy.key === 'pct')    return dir * (a.trend.pctChange - b.trend.pctChange)
      if (sortBy.key === 'name')   return dir * a.name.localeCompare(b.name)
      if (sortBy.key === 'rg')     return dir * a.rg.localeCompare(b.rg)
      if (sortBy.key === 'service') return dir * a.service.localeCompare(b.service)
      return 0
    })
  }, [resources, filterText, filterService, filterRg, sortBy])

  function Th({ label, k, right }: { label: string; k: string; right?: boolean }) {
    const sorted = sortBy.key === k
    return (
      <th data-sorted={sorted ? 'true' : 'false'}
        style={{ textAlign: right ? 'right' : 'left' }}
        onClick={() => setSortBy(s => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }))}>
        {label}
        <span className="sort-ind">{sorted ? (sortBy.dir === 'desc' ? '↓' : '↑') : '↕'}</span>
      </th>
    )
  }

  return (
    <div className="table-wrap">
      <table className="resources">
        <thead>
          <tr>
            <Th label="Resource" k="name" />
            <Th label="Service" k="service" />
            <Th label="Resource group" k="rg" />
            <Th label="Trend" k="pct" />
            <Th label="Period total" k="total" right />
            <Th label="Δ vs first half" k="change" right />
          </tr>
        </thead>
        <tbody>
          {filtered.slice(0, 200).map(r => {
            const t = r.trend
            const up = t.absChange > 0.001
            const color = up ? 'var(--up)' : t.absChange < -0.001 ? 'var(--down)' : 'var(--muted-2)'
            return (
              <tr key={r.id} onClick={() => onSelect(r)} data-selected={selected?.id === r.id ? 'true' : 'false'}>
                <td>
                  <div className="cell-name">
                    <ServiceIcon service={r.service} />
                    <span className="nm" title={r.name}>{r.name}</span>
                  </div>
                </td>
                <td><span className="muted">{r.service}</span></td>
                <td><span className="tag">{r.rg}</span></td>
                <td><Sparkline values={r.daily} width={64} height={20} color={color} strokeWidth={1.2} /></td>
                <td className="cost-cell">{fmtMoney(r.total)}</td>
                <td className="cost-cell" style={{ color }}>
                  {up ? '+' : ''}{fmtMoney(t.absChange)}
                </td>
              </tr>
            )
          })}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
                No resources match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ── Drill panel ────────────────────────────────────────────────────────────────

function DrillPanel({
  resource, data, onClose,
}: {
  resource: CostResource
  data: CostDashboard
  onClose: () => void
}) {
  const { meta } = data
  const t = resource.trend
  const up = t.absChange > 0.001
  const dailyAvg = meta.dates.length > 0 ? resource.total / meta.dates.length : 0

  return (
    <div className="drift-overlay" onClick={onClose}>
      <div className="drift-panel" onClick={e => e.stopPropagation()}>
        <div className="drift-panel-head">
          <div>
            <h2>{resource.name}</h2>
            <div className="dp-tags">
              <span className="dp-tag">{resource.service}</span>
              <span className="dp-tag">{resource.rg}</span>
              {resource.location && <span className="dp-tag">{resource.location}</span>}
            </div>
            <div className="dp-resid">{resource.id}</div>
          </div>
          <button className="dp-btn-icon" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </div>

        <div className="drift-panel-body">
          <div className="dp-stats">
            <div className="dp-stat">
              <div className="lbl">Period total</div>
              <div className="v">
                {fmtMoney(resource.total)}
                <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 4 }}>{meta.currency}</span>
              </div>
            </div>
            <div className="dp-stat">
              <div className="lbl">Daily avg</div>
              <div className="v">{fmtMoney(dailyAvg)}</div>
            </div>
            <div className="dp-stat">
              <div className="lbl">Trend</div>
              <div className="v" style={{ color: up ? 'var(--up)' : 'var(--down)' }}>
                {up ? '+' : ''}{fmtMoney(t.absChange)}
                <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 6 }}>
                  {isFinite(t.pctChange) ? (t.pctChange > 0 ? '+' : '') + fmtPct(t.pctChange) : ''}
                </span>
              </div>
            </div>
          </div>

          <div>
            <div className="dp-section-ttl">Daily spend</div>
            <TotalArea daily={resource.daily} dates={meta.dates} currency={meta.currency} height={200} />
          </div>

          <div>
            <div className="dp-section-ttl">Day-by-day</div>
            <table className="dp-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th style={{ textAlign: 'right' }}>Cost ({meta.currency})</th>
                  <th style={{ textAlign: 'right' }}>Share</th>
                </tr>
              </thead>
              <tbody>
                {resource.daily.map((v, i) => (
                  <tr key={i}>
                    <td className="dp-mono dp-muted">{fmtDate(meta.dates[i])}</td>
                    <td className="dp-cost-cell">{fmtMoney(v)}</td>
                    <td className="dp-cost-cell dp-muted">
                      {data.daily[i] > 0 ? ((v / data.daily[i]) * 100).toFixed(2) + '%' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────

type Range = '30d' | '90d' | 'ytd'
const RANGE_LABELS: Record<Range, string> = { '30d': '30 days', '90d': '90 days', 'ytd': 'YTD' }

export default function AzureCostPage() {
  const [range, setRange] = useState<Range>('30d')
  const [groupBy, setGroupBy] = useState<'service' | 'rg'>('service')
  const [selected, setSelected] = useState<CostResource | null>(null)
  const [filterText, setFilterText] = useState('')
  const [filterService, setFilterService] = useState('All')
  const [filterRg, setFilterRg] = useState('All')
  const [climberView, setClimberView] = useState<'climbers' | 'fallers'>('climbers')
  const [climberScope, setClimberScope] = useState<'rg' | 'resource'>('rg')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['azure-cost-dashboard', range],
    queryFn: () => api.get<CostDashboard>(`/azure-cost/dashboard?range=${range}`),
    staleTime: 15 * 60 * 1000,
    retry: false,
  })

  const climberRgs = useMemo(
    () => [...(data?.resourceGroups ?? [])].filter(r => r.total > 5).sort((a, b) => b.trend.absChange - a.trend.absChange),
    [data],
  )
  const climberRes = useMemo(
    () => [...(data?.resources ?? [])].filter(r => r.total > 2).sort((a, b) => b.trend.absChange - a.trend.absChange),
    [data],
  )
  const climbingCount = useMemo(
    () => (data?.resourceGroups ?? []).filter(r => r.trend.absChange > 0.5 && r.total > 5).length,
    [data],
  )

  const climberItems: ClimberItem[] = useMemo(() => {
    const base = climberScope === 'rg' ? climberRgs : climberRes
    const sorted = climberView === 'fallers' ? [...base].sort((a, b) => a.trend.absChange - b.trend.absChange) : base
    return sorted.slice(0, 7)
  }, [climberScope, climberView, climberRgs, climberRes])

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Finance</div>
          <h1 className="page-title">Azure Cost</h1>
          {data ? (
            <p className="page-subtitle">
              {fmtDate(data.meta.dates[0])} – {fmtDate(data.meta.dates[data.meta.dates.length - 1])}
              {' · '}{data.meta.currency}
              {data.meta.lastSyncAt && <> · synced {new Date(data.meta.lastSyncAt).toLocaleDateString('sv-SE')}</>}
            </p>
          ) : (
            <p className="page-subtitle">Cloud cost observability</p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--ns-surface-2)', border: '1px solid var(--ns-border)', borderRadius: 8 }}>
          {(Object.keys(RANGE_LABELS) as Range[]).map(r => (
            <button key={r} onClick={() => setRange(r)} style={{
              padding: '4px 10px', borderRadius: 5, border: 'none',
              background: range === r ? 'var(--ns-surface)' : 'transparent',
              boxShadow: range === r ? 'var(--ns-shadow-sm)' : 'none',
              fontSize: 12, fontWeight: 500, cursor: 'pointer',
              color: range === r ? 'var(--ns-text)' : 'var(--ns-text-muted)',
            }}>
              {RANGE_LABELS[r]}
            </button>
          ))}
        </div>
      </div>

      <div className="drift">
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: 'var(--muted)' }}>
            Loading cost data…
          </div>
        )}

        {isError && (
          <div className="card" style={{ padding: 32, textAlign: 'center' }}>
            <p style={{ color: 'var(--muted)', margin: 0 }}>Cost data not available.</p>
            <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 6, lineHeight: 1.6 }}>
              The daily sync pipeline must be deployed and have run at least once.<br />
              See <strong>WI-11881</strong> (Timer Trigger) and <strong>WI-11882</strong> (API endpoints).
            </p>
          </div>
        )}

        {data && (
          <>
            <KpiRow data={data} climberCount={climbingCount} />

            <HeroChart data={data} groupBy={groupBy} onGroupBy={setGroupBy} />

            <div className="row2">
              <div className="card">
                <div className="card-head">
                  <div>
                    <div className="card-title">{climberView === 'climbers' ? 'Cost climbers' : 'Cost fallers'}</div>
                    <div className="card-sub">
                      {climberView === 'climbers' ? 'Biggest rise vs. first half of period' : 'Biggest drop vs. first half of period'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div className="segmented">
                      <button data-active={climberScope === 'rg' ? 'true' : 'false'} onClick={() => setClimberScope('rg')}>Groups</button>
                      <button data-active={climberScope === 'resource' ? 'true' : 'false'} onClick={() => setClimberScope('resource')}>Resources</button>
                    </div>
                    <div className="segmented">
                      <button data-active={climberView === 'climbers' ? 'true' : 'false'} onClick={() => setClimberView('climbers')}>↑</button>
                      <button data-active={climberView === 'fallers' ? 'true' : 'false'} onClick={() => setClimberView('fallers')}>↓</button>
                    </div>
                  </div>
                </div>
                <Climbers
                  items={climberItems}
                  currency={data.meta.currency}
                  mode={climberView}
                  onSelect={it => {
                    if (climberScope === 'resource') {
                      setSelected(it as CostResource)
                    } else {
                      setFilterRg(it.name)
                      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                    }
                  }}
                />
              </div>

              <div className="card">
                <div className="card-head">
                  <div>
                    <div className="card-title">Service mix</div>
                    <div className="card-sub">Share of period spend · {data.services.length} services</div>
                  </div>
                  <span className="card-sub mono">{fmtMoney(data.meta.total)} {data.meta.currency}</span>
                </div>
                <ServiceBreakdown
                  services={data.services}
                  currency={data.meta.currency}
                  total={data.meta.total}
                  onSelect={s => {
                    setFilterService(s.name)
                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                  }}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-head" style={{ flexWrap: 'wrap', gap: 14 }}>
                <div>
                  <div className="card-title">Resources</div>
                  <div className="card-sub">{data.resources.length} resources · click a row to drill in</div>
                </div>
                <div className="filter-row">
                  <label className="search">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" />
                    </svg>
                    <input
                      placeholder="Search resources, groups…"
                      value={filterText}
                      onChange={e => setFilterText(e.target.value)}
                    />
                  </label>
                  <select
                    className="pill"
                    value={filterService}
                    onChange={e => setFilterService(e.target.value)}
                    style={{ appearance: 'none', paddingRight: 18, cursor: 'pointer' }}
                  >
                    <option>All</option>
                    {data.services.map(s => <option key={s.name}>{s.name}</option>)}
                  </select>
                  <select
                    className="pill"
                    value={filterRg}
                    onChange={e => setFilterRg(e.target.value)}
                    style={{ appearance: 'none', paddingRight: 18, cursor: 'pointer' }}
                  >
                    <option>All</option>
                    {data.resourceGroups.map(rg => <option key={rg.name}>{rg.name}</option>)}
                  </select>
                  {(filterText || filterService !== 'All' || filterRg !== 'All') && (
                    <button
                      className="pill"
                      onClick={() => { setFilterText(''); setFilterService('All'); setFilterRg('All') }}
                    >
                      Clear ✕
                    </button>
                  )}
                </div>
              </div>
              <ResourceTable
                resources={data.resources}
                currency={data.meta.currency}
                onSelect={setSelected}
                selected={selected}
                filterText={filterText}
                filterService={filterService}
                filterRg={filterRg}
              />
            </div>

            <footer style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 11, paddingBottom: 20 }}>
              Driftline · {data.meta.rowCount.toLocaleString()} cost-management rows · {data.meta.resourceCount} resources across {data.meta.rgCount} resource groups
            </footer>
          </>
        )}

        {selected && data && (
          <DrillPanel resource={selected} data={data} onClose={() => setSelected(null)} />
        )}
      </div>
    </div>
  )
}
